import React, { useContext, useMemo } from 'react'

import PlaceholderImage from './Common/PlaceholderImage'
import SummaryImage from './Common/SummaryImage'
import ReadOnlyItemFooter from './Common/SummaryModalFooter/ReadOnlyItemFooter'
import TourAndCruiseSummaryModalFooter from './Common/SummaryModalFooter/TourAndCruiseSummaryModalFooter'
import DeepLinkModalFooter from '../Common/DeepLinkModalFooter'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import CustomItemSummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/CustomItemSummaryContent'
import TourAndCruiseSummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/TourAndCruiseSummaryContent'
import TourV2SummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/TourV2SummaryContent'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { CruiseItem, TourItem } from 'tripPlanner/types/tripItem'
import {
  getOfferId,
  isLeBookmark,
  isTour,
  isTourOfferNoSummary,
} from 'tripPlanner/utils'
import { isBookableItem } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: CruiseItem | TourItem
}

const TourAndCruiseItemSummary = (props: Props) => {
  const { item } = props

  const { closeModal, goBack } = useModalCallbacks()

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false
  const canEdit = tripContext?.permissions.canEdit ?? false
  const isBookable = isLeBookmark(item) && isBookableItem(item)

  const offerId = getOfferId(item)
  const { data, isLoading } = useOffer(offerId)
  // Cast the type because we know it can't be `App.Bedbank`
  const offer = data as App.Offer | App.OfferSummary | Tours.V2Offer | undefined

  const modalContent = useMemo(() => {
    if (item.sourceType === 'NA') {
      const imageComponent = <SummaryImage place={item.startPlace} /> ?? (
        <PlaceholderImage itemType={item.type} />
      )

      return (
        <>
          {imageComponent}
          <CustomItemSummaryContent item={item} />
        </>
      )
    }
    if (isLoading) {
      return <LoadingIndicator inline />
    }
    if (offer && isTourOfferNoSummary(offer)) {
      return <TourAndCruiseSummaryContent item={item} offer={offer} />
    }
    if (offer && isTour(item) && isTourV2Offer(offer)) {
      return <TourV2SummaryContent item={item} offer={offer} />
    }
  }, [item, isLoading, offer])

  return (
    <>
      <ModalHeaderWrapper
        title={item.name}
        subtitle={item.startPlace.name}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>{modalContent}</ModalBody>

      {!viewAsTemplate && canEdit && (
        <>
          {isBookable && <DeepLinkModalFooter item={item} />}
          {!isBookable && <TourAndCruiseSummaryModalFooter item={item} />}
        </>
      )}
      {!viewAsTemplate && !canEdit && <ReadOnlyItemFooter item={item} />}
    </>
  )
}

export default TourAndCruiseItemSummary
